
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * BlockedIPEventLogGetFilterParams struct
 */
class BlockedIPEventLogGetFilterParams {
  /**
   * Initialize struct
   * @param {!Date=} fromInclusive
   * @param {!Date=} toExclusive
   * @param {!number=} minEncounteredTimes
   * @constructor
   */
  constructor (fromInclusive = new Date(0), toExclusive = new Date(0), minEncounteredTimes = new UInt64(0, 0)) {
    this.FromInclusive = fromInclusive
    this.ToExclusive = toExclusive
    this.MinEncounteredTimes = minEncounteredTimes
  }

  /**
   * Copy struct (shallow copy)
   * @this {!BlockedIPEventLogGetFilterParams}
   * @param {!BlockedIPEventLogGetFilterParams} other Other struct
   * @returns {!BlockedIPEventLogGetFilterParams} This struct
   */
  copy (other) {
    if (other.FromInclusive != null) {
      if (other.FromInclusive instanceof Date) {
        this.FromInclusive = new Date(other.FromInclusive.getTime())
      } else {
        this.FromInclusive = new Date(Math.round(other.FromInclusive / 1000000))
      }
    } else {
      this.FromInclusive = null
    }
    if (other.ToExclusive != null) {
      if (other.ToExclusive instanceof Date) {
        this.ToExclusive = new Date(other.ToExclusive.getTime())
      } else {
        this.ToExclusive = new Date(Math.round(other.ToExclusive / 1000000))
      }
    } else {
      this.ToExclusive = null
    }
    if (other.MinEncounteredTimes != null) {
      this.MinEncounteredTimes = UInt64.fromNumber(other.MinEncounteredTimes)
    } else {
      this.MinEncounteredTimes = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!BlockedIPEventLogGetFilterParams}
   * @returns {!BlockedIPEventLogGetFilterParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new BlockedIPEventLogGetFilterParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new BlockedIPEventLogGetFilterParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!BlockedIPEventLogGetFilterParams}
   * @param {!BlockedIPEventLogGetFilterParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof BlockedIPEventLogGetFilterParams)) {
      throw new TypeError('Instance of BlockedIPEventLogGetFilterParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!BlockedIPEventLogGetFilterParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      FromInclusive: ((this.FromInclusive != null) ? (this.FromInclusive.getTime() * 1000000) : null),
      ToExclusive: ((this.ToExclusive != null) ? (this.ToExclusive.getTime() * 1000000) : null),
      MinEncounteredTimes: ((this.MinEncounteredTimes != null) ? this.MinEncounteredTimes.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return BlockedIPEventLogGetFilterParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!BlockedIPEventLogGetFilterParams} other Object value
   * @returns {!BlockedIPEventLogGetFilterParams} Created struct
   */
  static fromObject (other) {
    return new BlockedIPEventLogGetFilterParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!BlockedIPEventLogGetFilterParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return BlockedIPEventLogGetFilterParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!BlockedIPEventLogGetFilterParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 3045
  }
}

export { BlockedIPEventLogGetFilterParams };

/**
 * Fast Binary Encoding BlockedIPEventLogGetFilterParams field model
 */
class FieldModelBlockedIPEventLogGetFilterParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.FromInclusive = new fbe.FieldModelTimestamp(buffer, 4 + 4)
    this.ToExclusive = new fbe.FieldModelTimestamp(buffer, this.FromInclusive.fbeOffset + this.FromInclusive.fbeSize)
    this.MinEncounteredTimes = new fbe.FieldModelUInt64(buffer, this.ToExclusive.fbeOffset + this.ToExclusive.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.FromInclusive.fbeSize + this.ToExclusive.fbeSize + this.MinEncounteredTimes.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.FromInclusive.fbeExtra + this.ToExclusive.fbeExtra + this.MinEncounteredTimes.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelBlockedIPEventLogGetFilterParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 3045
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.FromInclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.ToExclusive.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize

    if ((fbeCurrentSize + this.MinEncounteredTimes.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MinEncounteredTimes.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MinEncounteredTimes.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!BlockedIPEventLogGetFilterParams} fbeValue Default value, defaults is new BlockedIPEventLogGetFilterParams()
   * @returns {!BlockedIPEventLogGetFilterParams} BlockedIPEventLogGetFilterParams value
   */
  get (fbeValue = new BlockedIPEventLogGetFilterParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!BlockedIPEventLogGetFilterParams} fbeValue BlockedIPEventLogGetFilterParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.FromInclusive.fbeSize) <= fbeStructSize) {
      fbeValue.FromInclusive = this.FromInclusive.get()
    } else {
      fbeValue.FromInclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.FromInclusive.fbeSize

    if ((fbeCurrentSize + this.ToExclusive.fbeSize) <= fbeStructSize) {
      fbeValue.ToExclusive = this.ToExclusive.get()
    } else {
      fbeValue.ToExclusive = new Date(0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.ToExclusive.fbeSize

    if ((fbeCurrentSize + this.MinEncounteredTimes.fbeSize) <= fbeStructSize) {
      fbeValue.MinEncounteredTimes = this.MinEncounteredTimes.get()
    } else {
      fbeValue.MinEncounteredTimes = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MinEncounteredTimes.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {!BlockedIPEventLogGetFilterParams} fbeValue BlockedIPEventLogGetFilterParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelBlockedIPEventLogGetFilterParams}
   * @param {BlockedIPEventLogGetFilterParams} fbeValue BlockedIPEventLogGetFilterParams value
   */
  setFields (fbeValue) {
    this.FromInclusive.set(fbeValue.FromInclusive)
    this.ToExclusive.set(fbeValue.ToExclusive)
    this.MinEncounteredTimes.set(fbeValue.MinEncounteredTimes)
  }
}

export { FieldModelBlockedIPEventLogGetFilterParams };

/**
 * Fast Binary Encoding BlockedIPEventLogGetFilterParams model
 */
class BlockedIPEventLogGetFilterParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelBlockedIPEventLogGetFilterParams(this.buffer, 4)
  }

  /**
   * Get the BlockedIPEventLogGetFilterParams model
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!FieldModelBlockedIPEventLogGetFilterParams} model BlockedIPEventLogGetFilterParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return BlockedIPEventLogGetFilterParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelBlockedIPEventLogGetFilterParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @param {!BlockedIPEventLogGetFilterParams} value BlockedIPEventLogGetFilterParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @param {!BlockedIPEventLogGetFilterParams} value BlockedIPEventLogGetFilterParams value, defaults is new BlockedIPEventLogGetFilterParams()
   * @return {!object} Deserialized BlockedIPEventLogGetFilterParams value and its size
   */
  deserialize (value = new BlockedIPEventLogGetFilterParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new BlockedIPEventLogGetFilterParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new BlockedIPEventLogGetFilterParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!BlockedIPEventLogGetFilterParamsModel}
   * @param {!number} prev Previous BlockedIPEventLogGetFilterParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { BlockedIPEventLogGetFilterParamsModel };
